/**
 * Copy of the card refunds (Estornos) and ignore-with-reason flows of Classificação rápida de
 * cartão. Spread into `tenantSettings.cardFastTrack` (namespace `tenantSettings`), so every key
 * reads as `cardFastTrack.<block>.<key>`.
 *
 * `errors` holds one plain sentence per stable refusal code (see `@/lib/card-refund-errors`); the
 * coverage test proves both locales carry all of them. The sentences name no setup screen and no
 * vendor: they say what happened and what to do.
 */
export const cardRefundsPtBr = {
  refunds: {
    summary: '{{amount}} em estornos abertos',
    loadError: 'Não foi possível carregar os estornos.',
    incompleteMatches:
      'Os estornos ocupam mais de uma página. A marca "Tem estorno" nas compras pode estar incompleta.',
    pagination: {
      previous: 'Página anterior de estornos',
      next: 'Próxima página de estornos',
    },
    empty: {
      title: 'Nenhum estorno em aberto',
      description: 'Os estornos aparecem aqui até você lançar ou anular cada um.',
    },
    columns: {
      date: 'Data',
      merchant: 'Estabelecimento',
      amount: 'Valor',
      card: 'Cartão',
      status: 'Situação',
      actions: 'Ações',
    },
    cardLast4: 'final {{last4}}',
    iofRefund: 'Estorno de IOF',
    foreignAmount: '{{foreign}} · {{local}}',
    states: {
      POST_REFUND: 'Compra encontrada',
      PAIR: 'Compra pendente',
      AWAITING_APPROVAL: 'Aguardando aprovação',
      POST_PURCHASE_FIRST: 'Lance a compra primeiro',
      CHOOSE: 'Escolha a compra',
      NONE: 'Sem compra',
      LOAD_CANDIDATES: 'Buscar compra',
      POSTED: 'Estorno lançado',
      PAIRED: 'Compra anulada',
      HANDLED_ONFLY: 'Estornado pela OnFly',
      HANDLED_RECONCILIATION: 'Já conciliado',
      HANDLED_ELSEWHERE: 'Tratado em outro lugar',
    },
    stateHelp: {
      POST_REFUND: 'Achamos a compra deste estorno. Lance o estorno para reverter a compra.',
      PAIR: 'A compra ainda não foi lançada. Anule a compra e o estorno juntos, sem lançamento.',
      AWAITING_APPROVAL:
        'A fatura da compra espera aprovação. Aprove-a para poder lançar o estorno.',
      POST_PURCHASE_FIRST: 'Lance a compra na aba Prontas antes de lançar o estorno.',
      CHOOSE: 'Mais de uma compra serve. Escolha qual este estorno reverte.',
      NONE: 'Nenhuma compra deste estabelecimento foi encontrada. Lance o estorno informando as contas de despesa.',
      LOAD_CANDIDATES:
        'Ainda não buscamos a compra deste estorno. Use "Lançar estorno" para buscá-la.',
      POSTED: 'O estorno foi lançado no razão. Para voltar atrás, use "Desfazer".',
      PAIRED:
        'A compra e o estorno foram anulados, sem lançamento. Para voltar atrás, use "Desfazer".',
      HANDLED_ONFLY: 'A OnFly já lançou este estorno. Nada a fazer aqui.',
      HANDLED_RECONCILIATION:
        'Este estorno já foi tratado na tela de conciliação. Nada a fazer aqui.',
      HANDLED_ELSEWHERE: 'Este estorno já foi tratado de outra forma. Nada a fazer aqui.',
    },
    actions: {
      post: 'Lançar estorno',
      pair: 'Anular compra e estorno',
      undo: 'Desfazer',
      viewJournal: 'Ver lançamento',
    },
    purchase: {
      installment: 'Parcela {{number}}/{{total}}',
      bill: 'Fatura {{reference}}',
      remaining: 'Resta estornar {{amount}}',
      exactAmount: 'Valor igual ao estorno',
      sameInstallment: 'Mesma parcela',
      groupSummary_one: '{{merchant}} · {{count}} parcela · {{amount}} restantes',
      groupSummary_other: '{{merchant}} · {{count}} parcelas · {{amount}} restantes',
      linked: 'Compra: {{summary}}',
      linkedVendor: 'Fornecedor: {{vendor}}',
    },
    picker: {
      title: 'Qual compra este estorno reverte?',
      help: 'Marque a compra. Uma compra parcelada é uma só escolha: o estorno preenche as parcelas da mais antiga para a mais nova.',
      loading: 'Buscando compras...',
      loadError: 'Não foi possível buscar as compras deste estorno.',
      selectedSummary: 'A estornar nas compras marcadas: {{selected}} · Estorno: {{refund}}',
      noneSelected: 'Marque ao menos uma compra.',
    },
    none: {
      help: 'Não achamos a compra deste estorno. Informe o fornecedor e a conta de despesa que o estorno reduz.',
      vendor: 'Fornecedor',
      vendorHelp: 'O fornecedor que devolveu o dinheiro.',
      vendorPlaceholder: 'Selecione o fornecedor',
      splitsTitle: 'Onde o estorno reduz a despesa',
      splitsHelp:
        'Divida o valor do estorno entre as contas de despesa. A soma precisa ser igual ao estorno.',
      account: 'Conta de despesa',
      accountHelp:
        'A conta de despesa onde a compra original foi lançada. O estorno reduz essa despesa.',
      accountPlaceholder: 'Selecione a conta de despesa',
      amount: 'Valor',
      costCenter: 'Centro de custo',
      department: 'Departamento',
      managerialAccount: 'Conta gerencial',
      optional: 'opcional',
      addSplit: 'Adicionar conta de despesa',
      removeSplit: 'Remover conta',
      total: 'Soma',
      balanced: 'A soma confere com o estorno.',
      missing: 'Faltam {{amount}} para fechar o estorno.',
      over: 'Passou {{amount}} do valor do estorno.',
      incomplete: 'Informe o fornecedor, a conta de despesa e o valor de cada linha.',
      noAccess:
        'Para lançar um estorno sem a compra, você precisa de acesso às contas contábeis e aos fornecedores. Peça esse acesso ao administrador.',
    },
    dialog: {
      next: 'Continuar',
    },
    preview: {
      title: 'Conferir lançamento do estorno',
      description: 'Estas serão as linhas lançadas no razão. Confira antes de confirmar.',
      noAccess:
        'Para conferir e lançar o estorno, você precisa de acesso ao razão. Peça esse acesso ao administrador.',
      loading: 'Calculando o lançamento...',
      vendor: 'Fornecedor',
      account: 'Conta',
      costCenter: 'Centro de custo',
      debit: 'Débito',
      credit: 'Crédito',
      total: 'Total',
      cardPayable: 'Cartão a pagar',
      fxGain: 'Variação cambial ativa',
      fxLoss: 'Variação cambial passiva',
      confirm: 'Lançar estorno',
      confirming: 'Lançando...',
    },
    pair: {
      title: 'Anular compra e estorno',
      description:
        'A compra e o estorno se anulam. Nenhum lançamento é criado no razão e as duas linhas saem da lista.',
      confirm: 'Anular os dois',
      confirming: 'Anulando...',
    },
    undo: {
      titlePosted: 'Desfazer estorno',
      titlePaired: 'Desfazer anulação',
      descriptionPosted:
        'O lançamento do estorno será revertido e a linha volta para a lista de estornos em aberto.',
      descriptionPaired: 'A compra e o estorno voltam a ficar pendentes.',
      confirm: 'Desfazer',
      confirming: 'Desfazendo...',
    },
    journal: {
      subtitle: 'Lançamento do estorno',
    },
    badge: {
      label: 'Tem estorno',
      help: 'Esta compra tem um estorno esperando decisão. Veja na aba Estornos.',
    },
  },
  ignore: {
    action: 'Ignorar',
    bulkAction: 'Ignorar linhas',
    bulkDescription: 'Tirar compras da lista sem lançar',
    title_one: 'Ignorar esta linha',
    title_other: 'Ignorar {{count}} linhas',
    description_one:
      'A linha sai da lista de pendentes e vai para a aba Ignoradas. Nada é lançado.',
    description_other:
      'As linhas saem da lista de pendentes e vão para a aba Ignoradas. Nada é lançado.',
    reasonLabel: 'Motivo',
    reasons: {
      DUPLICATE: 'Duplicada',
      POSTED_ELSEWHERE: 'Lançada em outro lugar',
      OTHER: 'Outro',
    },
    reasonHelp: {
      DUPLICATE: 'A mesma compra aparece duas vezes.',
      POSTED_ELSEWHERE: 'Uma fatura paga com cartão já registrou esta compra.',
      OTHER: 'Explique o motivo na observação.',
    },
    relatedBillLabel: 'Fatura que já lançou esta compra',
    billSearchPlaceholder: 'Buscar fatura paga com cartão',
    billSearching: 'Buscando faturas...',
    billSearchEmpty: 'Nenhuma fatura paga com cartão encontrada.',
    billSearchError: 'Não foi possível buscar as faturas.',
    billClear: 'Trocar fatura',
    notesLabel: 'Observação',
    notesRequiredLabel: 'Observação (obrigatória)',
    notesPlaceholder: 'Escreva por que esta linha é ignorada',
    notesCount: '{{count}}/1000',
    confirm: 'Ignorar',
    confirming: 'Ignorando...',
    failedTitle: 'Estas linhas não foram ignoradas',
    failedLine: '{{merchant}} · {{date}} · {{amount}}',
    notEligible: 'Só compras pendentes e sem fatura podem ser ignoradas.',
  },
  ignored: {
    header: '{{amount}} ignorados nesta fatura',
    loadError: 'Não foi possível carregar as linhas ignoradas.',
    columns: {
      date: 'Data',
      merchant: 'Estabelecimento',
      amount: 'Valor',
      card: 'Cartão',
      reason: 'Motivo',
      ignoredBy: 'Ignorada por',
      ignoredAt: 'Ignorada em',
    },
    relatedBill: 'Fatura {{reference}}',
    empty: {
      title: 'Nenhuma linha ignorada',
      description: 'As linhas que você ignorar aparecem aqui e podem ser restauradas.',
    },
    selectAll: 'Selecionar todas as linhas',
    selectRow: 'Selecionar linha',
    restore: 'Restaurar',
    restoreBulk: 'Restaurar {{count}}',
    restoreTitle_one: 'Restaurar esta linha',
    restoreTitle_other: 'Restaurar {{count}} linhas',
    restoreDescription_one: 'A linha volta para a lista de pendentes.',
    restoreDescription_other: 'As linhas voltam para a lista de pendentes.',
    restoreConfirm: 'Restaurar',
    restoring: 'Restaurando...',
    failedTitle: 'Estas linhas não foram restauradas',
  },
  errors: {
    fallback: 'Não foi possível concluir. Atualize a lista e tente novamente.',
    CARD_REFUND_ACCOUNT_HIDDEN:
      'Este estorno usa uma conta contábil que você não pode ver. Peça a alguém com acesso a essa conta para lançá-lo.',
    CARD_REFUND_ACCOUNT_NOT_ALLOWED:
      'Escolha uma conta de despesa ou de custo. O estorno só reduz esse tipo de conta.',
    CARD_REFUND_ACCOUNT_UNMAPPED:
      'Uma conta da compra não tem conta equivalente ativa no período do estorno. Lance o estorno como lançamento manual.',
    CARD_REFUND_ALREADY_LINKED: 'Este estorno já foi tratado de outra forma. Desfaça-o primeiro.',
    CARD_REFUND_ALREADY_REVERSED:
      'Parte desta compra já foi revertida. Lance o estorno como lançamento manual.',
    CARD_REFUND_BOOK_CURRENCY_UNSUPPORTED:
      'Estornos de cartão só podem ser lançados em livros mantidos na moeda funcional da entidade.',
    CARD_REFUND_CAPITALIZED_LINE:
      'Esta compra foi lançada em conta de balanço. Lance o estorno como lançamento manual.',
    CARD_REFUND_CARD_CONTEXT_NOT_FOUND:
      'Não encontramos o cartão desta linha para esta entidade. Atualize a lista.',
    CARD_REFUND_CARD_CURRENCY_UNSUPPORTED:
      'Só é possível tratar estornos de cartões faturados na moeda funcional da entidade.',
    CARD_REFUND_DEFERRED_BILL:
      'A compra está diferida. Cancele o diferimento antes de lançar o estorno.',
    CARD_REFUND_DIMENSION_NOT_USABLE:
      'Um centro de custo, departamento ou conta gerencial está inativo ou não vale para esta entidade. Escolha outro.',
    CARD_REFUND_EXCEEDS_REMAINING:
      'O estorno é maior do que ainda resta estornar nas compras escolhidas, ou menor do que o necessário para creditar uma delas.',
    CARD_REFUND_ITEM_CANCELLED:
      'A operadora cancelou esta linha do cartão, ou ela é cópia de outra. Ela não entra na fatura.',
    CARD_REFUND_ITEM_NOT_PENDING: 'Esta linha do cartão já foi tratada. Atualize a lista.',
    CARD_REFUND_MIXED_CURRENCY_BASIS:
      'O estorno e a compra precisam estar os dois na moeda funcional ou na mesma moeda estrangeira.',
    CARD_REFUND_NOT_A_REFUND: 'Esta linha não é um estorno.',
    CARD_REFUND_NOT_LINKED: 'Este estorno não tem lançamento ou anulação para desfazer.',
    CARD_REFUND_PAIR_NOT_ELIGIBLE:
      'Só dá para anular uma compra pendente de mesmo valor, na mesma fatura e no mesmo mês. Lance a compra primeiro.',
    CARD_REFUND_PURCHASE_BILL_MISMATCH:
      'A fatura da compra não confere mais com a linha do cartão. Revise a fatura.',
    CARD_REFUND_PURCHASE_BILL_NOT_FOUND: 'Não encontramos a fatura da compra. Atualize a lista.',
    CARD_REFUND_PURCHASE_NOT_ELIGIBLE:
      'A compra precisa ser anterior ao estorno e ter sido feita no mesmo cartão.',
    CARD_REFUND_PURCHASE_NOT_FOUND: 'Escolha a compra que este estorno reverte.',
    CARD_REFUND_PURCHASE_NOT_POSTED:
      'A compra ainda não foi lançada. Lance a compra antes do estorno.',
    CARD_REFUND_PURCHASE_NOT_SETTLED:
      'A conciliação da compra no cartão foi desfeita. Concilie a compra de novo antes do estorno.',
    CARD_REFUND_PURCHASE_RECLASSIFIED:
      'A despesa desta compra foi transferida para outra conta. Lance o estorno como lançamento manual.',
    CARD_REFUND_PREVIEW_GL_READ_REQUIRED:
      'Para conferir o lançamento do estorno, você precisa de acesso ao razão. Peça esse acesso ao administrador.',
    CARD_REFUND_SPLIT_TOTAL_MISMATCH: 'A soma das contas precisa ser igual ao valor do estorno.',
    CARD_REFUND_TAX_EXPOSURE:
      'A compra tem imposto retido ou a recuperar. Lance o estorno como lançamento manual.',
    CARD_REFUND_VENDOR_REQUIRED: 'Escolha o fornecedor do estorno.',
    CARD_IGNORE_FAILED: 'Não foi possível ignorar esta linha. Atualize a lista e tente de novo.',
    CARD_IGNORE_RESTORE_FAILED:
      'Não foi possível restaurar esta linha. Atualize a lista e tente de novo.',
    CARD_IGNORE_ITEM_HAS_BILL:
      'Esta linha já tem fatura. Cancele a fatura em vez de ignorar a linha.',
    CARD_IGNORE_ITEM_HAS_REFUND: 'Esta linha tem estorno ligado. Desfaça o estorno primeiro.',
    CARD_IGNORE_ITEM_NOT_FOUND: 'Linha do cartão não encontrada. Atualize a lista.',
    CARD_IGNORE_ITEM_NOT_IGNORED: 'Esta linha não está ignorada.',
    CARD_IGNORE_ITEM_NOT_PENDING: 'Esta linha já foi tratada. Atualize a lista.',
    CARD_IGNORE_NOTES_REQUIRED: 'Escreva por que a linha é ignorada.',
    CARD_IGNORE_NOT_A_PURCHASE: 'Só compras podem ser ignoradas.',
    CARD_IGNORE_REASON_INVALID: 'Escolha o motivo.',
    CARD_IGNORE_RELATED_BILL_NOT_CARD_PAID: 'Escolha uma fatura que foi paga com cartão.',
    CARD_IGNORE_RELATED_BILL_OTHER_CARD: 'Escolha uma fatura paga com este mesmo cartão.',
    CARD_IGNORE_RELATED_BILL_REQUIRED: 'Escolha a fatura que já lançou esta compra.',
    BILL_HAS_ACTIVE_CARD_REFUND:
      'Esta fatura tem estorno de cartão lançado. Desfaça o estorno antes de revertê-la.',
    ONFLY_CARD_REFUND_MANUAL_REFUND_ACTIVE:
      'Este estorno já foi tratado manualmente. Desfaça-o antes de reprocessar.',
  },
};

export const cardRefundsEn = {
  refunds: {
    summary: '{{amount}} in open refunds',
    loadError: 'Could not load the refunds.',
    incompleteMatches:
      'The refunds take more than one page. The "Has refund" mark on purchases may be incomplete.',
    pagination: {
      previous: 'Previous page of refunds',
      next: 'Next page of refunds',
    },
    empty: {
      title: 'No open refunds',
      description: 'Refunds show up here until you post or cancel out each one.',
    },
    columns: {
      date: 'Date',
      merchant: 'Merchant',
      amount: 'Amount',
      card: 'Card',
      status: 'Status',
      actions: 'Actions',
    },
    cardLast4: 'ending {{last4}}',
    iofRefund: 'IOF refund',
    foreignAmount: '{{foreign}} · {{local}}',
    states: {
      POST_REFUND: 'Purchase found',
      PAIR: 'Purchase pending',
      AWAITING_APPROVAL: 'Awaiting approval',
      POST_PURCHASE_FIRST: 'Post the purchase first',
      CHOOSE: 'Choose the purchase',
      NONE: 'No purchase',
      LOAD_CANDIDATES: 'Find purchase',
      POSTED: 'Refund posted',
      PAIRED: 'Purchase cancelled out',
      HANDLED_ONFLY: 'Refunded by OnFly',
      HANDLED_RECONCILIATION: 'Already reconciled',
      HANDLED_ELSEWHERE: 'Handled elsewhere',
    },
    stateHelp: {
      POST_REFUND: 'We found the purchase of this refund. Post the refund to reverse the purchase.',
      PAIR: 'The purchase is not posted yet. Cancel the purchase and the refund out together, with no entry.',
      AWAITING_APPROVAL:
        "The purchase's bill is waiting for approval. Approve it to be able to post the refund.",
      POST_PURCHASE_FIRST: 'Post the purchase in the Ready tab before posting the refund.',
      CHOOSE: 'More than one purchase fits. Choose the one this refund reverses.',
      NONE: 'No purchase from this merchant was found. Post the refund by entering the expense accounts.',
      LOAD_CANDIDATES:
        'We have not looked for the purchase of this refund yet. Use "Post refund" to look for it.',
      POSTED: 'The refund was posted to the ledger. To go back, use "Undo".',
      PAIRED:
        'The purchase and the refund were cancelled out, with no entry. To go back, use "Undo".',
      HANDLED_ONFLY: 'OnFly already posted this refund. Nothing to do here.',
      HANDLED_RECONCILIATION:
        'This refund was already handled on the reconciliation screen. Nothing to do here.',
      HANDLED_ELSEWHERE: 'This refund was already handled another way. Nothing to do here.',
    },
    actions: {
      post: 'Post refund',
      pair: 'Cancel purchase and refund',
      undo: 'Undo',
      viewJournal: 'View entry',
    },
    purchase: {
      installment: 'Installment {{number}}/{{total}}',
      bill: 'Bill {{reference}}',
      remaining: '{{amount}} left to refund',
      exactAmount: 'Same amount as the refund',
      sameInstallment: 'Same installment',
      groupSummary_one: '{{merchant}} · {{count}} installment · {{amount}} left',
      groupSummary_other: '{{merchant}} · {{count}} installments · {{amount}} left',
      linked: 'Purchase: {{summary}}',
      linkedVendor: 'Vendor: {{vendor}}',
    },
    picker: {
      title: 'Which purchase does this refund reverse?',
      help: 'Tick the purchase. An installment purchase is one choice: the refund fills the installments from the oldest to the newest.',
      loading: 'Looking for purchases...',
      loadError: 'Could not look up the purchases of this refund.',
      selectedSummary: 'To refund on the ticked purchases: {{selected}} · Refund: {{refund}}',
      noneSelected: 'Tick at least one purchase.',
    },
    none: {
      help: 'We did not find the purchase of this refund. Enter the vendor and the expense account the refund reduces.',
      vendor: 'Vendor',
      vendorHelp: 'The supplier that returned the money.',
      vendorPlaceholder: 'Select the vendor',
      splitsTitle: 'Where the refund reduces the expense',
      splitsHelp: 'Split the refund between expense accounts. The sum must equal the refund.',
      account: 'Expense account',
      accountHelp:
        'The expense account where the original purchase was booked. The refund reduces this expense.',
      accountPlaceholder: 'Select the expense account',
      amount: 'Amount',
      costCenter: 'Cost center',
      department: 'Department',
      managerialAccount: 'Managerial account',
      optional: 'optional',
      addSplit: 'Add expense account',
      removeSplit: 'Remove account',
      total: 'Sum',
      balanced: 'The sum matches the refund.',
      missing: '{{amount}} still missing to match the refund.',
      over: '{{amount}} over the refund amount.',
      incomplete: 'Enter the vendor, the expense account and the amount of every line.',
      noAccess:
        'To post a refund without its purchase, you need access to GL accounts and vendors. Ask your administrator for this access.',
    },
    dialog: {
      next: 'Continue',
    },
    preview: {
      title: 'Review the refund entry',
      description:
        'These are the lines that will post to the ledger. Check them before confirming.',
      noAccess:
        'To review and post the refund, you need access to the general ledger. Ask your administrator for this access.',
      loading: 'Working out the entry...',
      vendor: 'Vendor',
      account: 'Account',
      costCenter: 'Cost center',
      debit: 'Debit',
      credit: 'Credit',
      total: 'Total',
      cardPayable: 'Card payable',
      fxGain: 'Foreign exchange gain',
      fxLoss: 'Foreign exchange loss',
      confirm: 'Post refund',
      confirming: 'Posting...',
    },
    pair: {
      title: 'Cancel purchase and refund',
      description:
        'The purchase and the refund cancel each other out. No entry is created in the ledger and both lines leave the list.',
      confirm: 'Cancel both out',
      confirming: 'Cancelling out...',
    },
    undo: {
      titlePosted: 'Undo refund',
      titlePaired: 'Undo cancelling out',
      descriptionPosted:
        'The refund entry will be reversed and the line goes back to the open refunds list.',
      descriptionPaired: 'The purchase and the refund become pending again.',
      confirm: 'Undo',
      confirming: 'Undoing...',
    },
    journal: {
      subtitle: 'Refund entry',
    },
    badge: {
      label: 'Has refund',
      help: 'This purchase has a refund waiting for a decision. See the Refunds tab.',
    },
  },
  ignore: {
    action: 'Ignore',
    bulkAction: 'Ignore lines',
    bulkDescription: 'Take purchases off the list without posting',
    title_one: 'Ignore this line',
    title_other: 'Ignore {{count}} lines',
    description_one:
      'The line leaves the pending list and moves to the Ignored tab. Nothing is posted.',
    description_other:
      'The lines leave the pending list and move to the Ignored tab. Nothing is posted.',
    reasonLabel: 'Reason',
    reasons: {
      DUPLICATE: 'Duplicate',
      POSTED_ELSEWHERE: 'Posted elsewhere',
      OTHER: 'Other',
    },
    reasonHelp: {
      DUPLICATE: 'The same purchase shows up twice.',
      POSTED_ELSEWHERE: 'A bill paid by card already recorded this purchase.',
      OTHER: 'Explain the reason in the note.',
    },
    relatedBillLabel: 'Bill that already recorded this purchase',
    billSearchPlaceholder: 'Search card-paid bills',
    billSearching: 'Searching bills...',
    billSearchEmpty: 'No card-paid bill found.',
    billSearchError: 'Could not search the bills.',
    billClear: 'Change bill',
    notesLabel: 'Note',
    notesRequiredLabel: 'Note (required)',
    notesPlaceholder: 'Write why this line is ignored',
    notesCount: '{{count}}/1000',
    confirm: 'Ignore',
    confirming: 'Ignoring...',
    failedTitle: 'These lines were not ignored',
    failedLine: '{{merchant}} · {{date}} · {{amount}}',
    notEligible: 'Only pending purchases with no bill can be ignored.',
  },
  ignored: {
    header: '{{amount}} ignored on this statement',
    loadError: 'Could not load the ignored lines.',
    columns: {
      date: 'Date',
      merchant: 'Merchant',
      amount: 'Amount',
      card: 'Card',
      reason: 'Reason',
      ignoredBy: 'Ignored by',
      ignoredAt: 'Ignored on',
    },
    relatedBill: 'Bill {{reference}}',
    empty: {
      title: 'No ignored lines',
      description: 'Lines you ignore show up here and can be restored.',
    },
    selectAll: 'Select all lines',
    selectRow: 'Select line',
    restore: 'Restore',
    restoreBulk: 'Restore {{count}}',
    restoreTitle_one: 'Restore this line',
    restoreTitle_other: 'Restore {{count}} lines',
    restoreDescription_one: 'The line goes back to the pending list.',
    restoreDescription_other: 'The lines go back to the pending list.',
    restoreConfirm: 'Restore',
    restoring: 'Restoring...',
    failedTitle: 'These lines were not restored',
  },
  errors: {
    fallback: 'Could not finish. Refresh the list and try again.',
    CARD_REFUND_ACCOUNT_HIDDEN:
      'This refund uses a GL account you cannot see. Ask someone with access to that account to post it.',
    CARD_REFUND_ACCOUNT_NOT_ALLOWED:
      'Choose an expense or cost account. A refund only reduces that kind of account.',
    CARD_REFUND_ACCOUNT_UNMAPPED:
      'An account of the purchase has no active matching account in the refund period. Post the refund as a manual entry.',
    CARD_REFUND_ALREADY_LINKED: 'This refund was already handled another way. Undo it first.',
    CARD_REFUND_ALREADY_REVERSED:
      'Part of this purchase was already reversed. Post the refund as a manual entry.',
    CARD_REFUND_BOOK_CURRENCY_UNSUPPORTED:
      "Card refunds can only be posted to books kept in the entity's functional currency.",
    CARD_REFUND_CAPITALIZED_LINE:
      'This purchase was posted to a balance sheet account. Post the refund as a manual entry.',
    CARD_REFUND_CARD_CONTEXT_NOT_FOUND:
      'We could not find the card of this line for this entity. Refresh the list.',
    CARD_REFUND_CARD_CURRENCY_UNSUPPORTED:
      "Only refunds on cards billed in the entity's functional currency can be handled here.",
    CARD_REFUND_DEFERRED_BILL:
      'The purchase is deferred. Cancel the deferral before posting the refund.',
    CARD_REFUND_DIMENSION_NOT_USABLE:
      'A cost center, department or managerial account is inactive or not valid for this entity. Choose another.',
    CARD_REFUND_EXCEEDS_REMAINING:
      'The refund is larger than what is left to refund on the chosen purchases, or too small to credit one of them.',
    CARD_REFUND_ITEM_CANCELLED:
      'The card provider cancelled this card line, or it is a copy of another line. It is not on the statement.',
    CARD_REFUND_ITEM_NOT_PENDING: 'This card line was already handled. Refresh the list.',
    CARD_REFUND_MIXED_CURRENCY_BASIS:
      'The refund and the purchase must both be in the functional currency or in the same foreign currency.',
    CARD_REFUND_NOT_A_REFUND: 'This line is not a refund.',
    CARD_REFUND_NOT_LINKED: 'This refund has no entry or cancelling out to undo.',
    CARD_REFUND_PAIR_NOT_ELIGIBLE:
      'Only a pending purchase of the same amount, on the same statement and in the same month can be cancelled out. Post the purchase first.',
    CARD_REFUND_PURCHASE_BILL_MISMATCH:
      'The purchase bill no longer matches the card line. Review the bill.',
    CARD_REFUND_PURCHASE_BILL_NOT_FOUND:
      'We could not find the bill of the purchase. Refresh the list.',
    CARD_REFUND_PURCHASE_NOT_ELIGIBLE:
      'The purchase must come before the refund and be on the same card.',
    CARD_REFUND_PURCHASE_NOT_FOUND: 'Choose the purchase this refund reverses.',
    CARD_REFUND_PURCHASE_NOT_POSTED: 'The purchase is not posted yet. Post it before the refund.',
    CARD_REFUND_PURCHASE_NOT_SETTLED:
      'The card reconciliation of the purchase was undone. Reconcile the purchase again before the refund.',
    CARD_REFUND_PURCHASE_RECLASSIFIED:
      'The expense of this purchase was moved to another account. Post the refund as a manual entry.',
    CARD_REFUND_PREVIEW_GL_READ_REQUIRED:
      'To review the refund entry, you need access to the general ledger. Ask your administrator for this access.',
    CARD_REFUND_SPLIT_TOTAL_MISMATCH: 'The accounts must add up to the refund amount.',
    CARD_REFUND_TAX_EXPOSURE:
      'The purchase has withheld or recoverable tax. Post the refund as a manual entry.',
    CARD_REFUND_VENDOR_REQUIRED: 'Choose the vendor of the refund.',
    CARD_IGNORE_FAILED: 'Could not ignore this line. Refresh the list and try again.',
    CARD_IGNORE_RESTORE_FAILED: 'Could not restore this line. Refresh the list and try again.',
    CARD_IGNORE_ITEM_HAS_BILL:
      'This line already has a bill. Cancel the bill instead of ignoring the line.',
    CARD_IGNORE_ITEM_HAS_REFUND: 'This line has a refund linked. Undo the refund first.',
    CARD_IGNORE_ITEM_NOT_FOUND: 'Card line not found. Refresh the list.',
    CARD_IGNORE_ITEM_NOT_IGNORED: 'This line is not ignored.',
    CARD_IGNORE_ITEM_NOT_PENDING: 'This line was already handled. Refresh the list.',
    CARD_IGNORE_NOTES_REQUIRED: 'Write why the line is ignored.',
    CARD_IGNORE_NOT_A_PURCHASE: 'Only purchases can be ignored.',
    CARD_IGNORE_REASON_INVALID: 'Choose the reason.',
    CARD_IGNORE_RELATED_BILL_NOT_CARD_PAID: 'Choose a bill that was paid by card.',
    CARD_IGNORE_RELATED_BILL_OTHER_CARD: 'Choose a bill paid with this same card.',
    CARD_IGNORE_RELATED_BILL_REQUIRED: 'Choose the bill that already recorded this purchase.',
    BILL_HAS_ACTIVE_CARD_REFUND:
      'This bill has a posted card refund. Undo the refund before reversing the bill.',
    ONFLY_CARD_REFUND_MANUAL_REFUND_ACTIVE:
      'This refund was already handled manually. Undo it before reprocessing.',
  },
};
